import { useEffect, useState } from "react";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { toast } from "sonner";
import { CalendarClock, ExternalLink, Trash2, UserRound, Users } from "lucide-react";
import { JobContacts } from "@/components/people/JobContacts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CompanyLogo, NativeSelect, PageHeader, Spinner } from "@/components/Common";
import { JobDetailSheet } from "@/components/JobDetailSheet";
import { AtsBadge } from "@/components/AtsBadge";
import { TailorResumeButton } from "@/components/TailorResumeButton";
import { RejectedBanner } from "@/components/WbNav";
import { StreakCard } from "@/components/StreakCard";
import { TodayPlanCard } from "@/components/plan/TodayPlanCard";
import { ReadyFilter, useReadyToggle } from "@/components/ReadyFilter";
import { isReady, useAtsTick } from "@/lib/atsScores";
import { api, asList, enc, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export const STATUSES = ["saved", "applied", "interviewing", "offer", "rejected"];
const DOT = { saved: "bg-[#D4AF37]", applied: "bg-sky-400", interviewing: "bg-violet-400", offer: "bg-emerald-400", rejected: "bg-red-400" };
const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 16); };

const EditDialog = ({ app, onClose, onSaved, onDelete, onView, onContacts }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (app) setF({ status: app.status, notes: app.notes || "", recruiter_name: app.recruiter_name || "", recruiter_email: app.recruiter_email || "", recruiter_phone: app.recruiter_phone || "", interview_at: toLocal(app.interview_at) });
  }, [app]);
  if (!app || !f) return null;
  const save = async () => {
    try {
      const body = { ...f, interview_at: f.interview_at ? new Date(f.interview_at).toISOString() : null };
      const { data } = await api.patch(`/applications/${enc(app.job_id)}`, body);
      onSaved({ ...app, ...body, ...(data && typeof data === "object" ? data : {}) });
      toast.success("Updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label, type = "text") => (
    <div className="space-y-1.5"><Label className="text-xs text-zinc-400">{label}</Label><Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-[#09090B]" data-testid={`edit-${k}`} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto border-[#27272A] bg-[#18181B] sm:max-w-lg" data-testid="application-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">{app.job?.title}<div className="text-sm font-normal text-zinc-400">{app.job?.company}</div></DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs text-zinc-400">Status</Label>
            <NativeSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUSES.map((s) => ({ value: s, label: s }))} className="w-full capitalize" data-testid="edit-status" /></div>
          {field("recruiter_name", "Recruiter name")}
          {field("recruiter_email", "Recruiter email", "email")}
          {field("recruiter_phone", "Recruiter phone")}
          {field("interview_at", "Interview at", "datetime-local")}
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs text-zinc-400">Notes</Label><Textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="bg-[#09090B]" data-testid="edit-notes" /></div>
          <div className="sm:col-span-2"><JobContacts jobId={app.job_id} job={app.job} contacts={app.contacts || []} onList={onContacts} /></div>
        </div>
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" className="text-red-400" onClick={() => onDelete(app)} data-testid="delete-application-button"><Trash2 className="h-4 w-4" />Delete</Button>
            <Button variant="ghost" onClick={() => onView(app.job_id)} data-testid="view-job-button"><ExternalLink className="h-4 w-4" />View job</Button>
          </div>
          <Button onClick={save} data-testid="save-application-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KanbanCard = ({ a, onClick }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", a.job_id)} onClick={onClick} data-testid={`kanban-card-${a.job_id}`}
    className="cursor-grab rounded-lg border border-[#27272A] bg-[#18181B] p-3 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-[#D4AF37]/40 active:cursor-grabbing">
    <div className="flex gap-3">
      <CompanyLogo src={a.job?.logo} name={a.job?.company} size="h-8 w-8" />
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-zinc-100">{a.job?.title}</div>
        <div className="truncate text-xs text-zinc-500">{a.job?.company}</div>
      </div>
    </div>
    <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-zinc-500">
      {a.interview_at && <span className="flex items-center gap-1 text-violet-300"><CalendarClock className="h-3 w-3" />{new Date(a.interview_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>}
      {a.recruiter_name && <span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{a.recruiter_name}</span>}
      {a.contacts?.length > 0 && <span className="flex items-center gap-1 text-[#F3C649]" data-testid={`kanban-contacts-${a.job_id}`}><Users className="h-3 w-3" />{a.contacts.length} contact{a.contacts.length === 1 ? "" : "s"}</span>}
      {a.match_score != null && <span className="font-mono text-[#F3C649]">{a.match_score}%</span>}
      <AtsBadge jobId={a.job_id} />
      {a.status !== "rejected" && <TailorResumeButton job={{ ...(a.job || {}), id: a.job_id }} compact />}
    </div>
    {a.notes && <p className="mt-2 line-clamp-2 text-xs text-zinc-500">{a.notes}</p>}
  </div>
);

export default function Tracker() {
  const [apps, setApps] = useState(null);
  const [edit, setEdit] = useState(null);
  const [over, setOver] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [ready, setReady] = useReadyToggle("tracker");
  const passMin = useAtsTick();
  const load = () => api.get("/applications").then((r) => setApps(asList(r.data, "applications"))).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const visible = (apps || []).filter((a) => !ready || isReady(a.job_id, passMin));

  const move = async (jobId, status) => {
    const prev = apps;
    setApps((a) => a.map((x) => (x.job_id === jobId ? { ...x, status } : x)));
    try { await api.patch(`/applications/${enc(jobId)}`, { status }); } catch (e) { setApps(prev); toast.error(errMsg(e)); }
  };
  const del = async (a) => {
    try { await api.delete(`/applications/${enc(a.job_id)}`); setApps((x) => x.filter((y) => y.job_id !== a.job_id)); setEdit(null); toast.success("Deleted"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Tracker" subtitle="Drag cards between stages. Click a card to log recruiter details and interview times." />
      <HumanSupportBanner where="tracker" />
      <StreakCard />
      <TodayPlanCard />
      {apps && <div className="mb-4"><ReadyFilter on={ready} onChange={setReady} min={passMin} count={visible.length} />{ready && visible.length === 0 && <span className="ml-3 text-xs text-zinc-500" data-testid="tracker-ready-empty">No ready jobs yet — run an ATS check on a tracked job to see if it's ready.</span>}</div>}
      {!apps ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : (
        <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-4 md:mx-0 md:px-0" data-testid="kanban-board">
          {STATUSES.map((s) => {
            const items = visible.filter((a) => a.status === s);
            return (
              <div key={s} data-testid={`kanban-col-${s}`}
                onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver(null)}
                onDrop={(e) => { e.preventDefault(); setOver(null); const id = e.dataTransfer.getData("text/plain"); if (id) move(id, s); }}
                className={cn("kanban-col flex min-h-[60vh] w-72 shrink-0 flex-col rounded-xl border border-[#27272A] bg-[#0f0f12] p-3 transition-colors xl:w-auto xl:flex-1", over === s && "drag-over")}>
                <div className="mb-3 flex items-center justify-between px-1">
                  <div className="flex items-center gap-2"><span className={cn("h-2 w-2 rounded-full", DOT[s])} /><span className="text-xs font-semibold uppercase tracking-widest text-zinc-300">{s}</span></div>
                  <span className="font-mono text-xs text-zinc-500" data-testid={`kanban-count-${s}`}>{items.length}</span>
                </div>
                {s === "rejected" && items.length > 0 && <RejectedBanner />}
                <div className="flex flex-col gap-2">{items.map((a) => <KanbanCard key={a.job_id} a={a} onClick={() => setEdit(a)} />)}</div>
              </div>
            );
          })}
        </div>
      )}
      <EditDialog app={edit} onClose={() => setEdit(null)} onDelete={del} onView={(id) => { setEdit(null); setOpenId(id); }}
        onContacts={(list) => setApps((a) => a.map((x) => (x.job_id === edit?.job_id ? { ...x, contacts: list } : x)))}
        onSaved={(u) => { setApps((a) => a.map((x) => (x.job_id === u.job_id ? { ...u, contacts: x.contacts } : x))); setEdit(null); }} />
      <JobDetailSheet jobId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
